/**
 * Normalises what a user types into the "server address" field.
 *
 * - `/abs` or `abs` style relative paths resolve against the current origin
 *   (used with the bundled same-origin proxy).
 * - Bare hosts default to https://, except obvious LAN and Tailscale addresses
 *   (including single-label names like `tower`), which default to http://
 *   because home servers rarely have certificates there.
 * - Query strings, fragments and trailing slashes are removed.
 */
export function normalizeServerUrl(input: string, origin = globalThis.location?.origin ?? ''): string {
  let raw = input.trim();
  if (!raw) throw new Error('Enter your Audiobookshelf server address.');

  if (raw.startsWith('/')) {
    if (!origin) throw new Error('Relative addresses need a browser origin.');
    raw = origin + raw;
  } else if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
    raw = (isLikelyLan(raw) ? 'http://' : 'https://') + raw;
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new Error('That does not look like a valid address.');
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error('Only http:// and https:// addresses are supported.');
  }
  if (url.username || url.password) {
    throw new Error('Do not put credentials in the server address.');
  }
  url.search = '';
  url.hash = '';
  return url.toString().replace(/\/+$/, '');
}

function isLikelyLan(hostAndPath: string): boolean {
  const host = (hostAndPath.split(/[/:]/)[0] ?? '').toLowerCase();
  return (
    host === 'localhost' ||
    // Single-label names like Unraid's default "tower" only resolve on the LAN.
    (host !== '' && !host.includes('.')) ||
    // Tailscale addresses (100.64.0.0/10): plain http, WireGuard encrypts the link.
    /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(host) ||
    host.endsWith('.local') ||
    host.endsWith('.lan') ||
    host.endsWith('.home.arpa') ||
    /^10\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host) ||
    /^127\./.test(host)
  );
}

/** Resolves a server-relative path (e.g. `/api/items/x/file/1`) against the base URL. */
export function resolveServerPath(baseUrl: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return baseUrl.replace(/\/+$/, '') + (path.startsWith('/') ? path : `/${path}`);
}

/** Adds query parameters, skipping undefined/null values. */
export function withQuery(url: string, params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return url;
  const qs = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
  return url + (url.includes('?') ? '&' : '?') + qs;
}

/** True when a mixed-content block is certain: an https page talking to an http server. */
export function isMixedContent(serverUrl: string, pageProtocol = globalThis.location?.protocol): boolean {
  return pageProtocol === 'https:' && serverUrl.startsWith('http://');
}
